
import { Link } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { Hero, Features, Pricing, Testimonials } from '../components/landing';
import Logo from '../components/ui/Logo';

const LandingPage: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <nav className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo avec le composant */}
            <Logo size="xl" showText={false} className="hover:opacity-80 transition-opacity" />
            
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">
                Fonctionnalités
              </a>
              <a href="#pricing" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">
                Tarifs
              </a>
              <a href="#testimonials" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">
                Témoignages
              </a>
              <a href="#how-it-works" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">
                Comment ça marche
              </a>
            </div>
            
            <div className="flex items-center space-x-4">
              <Link 
                to="/login" 
                className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                Connexion
              </Link>
              <Link 
                to="/register" 
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-transform"
              >
                Essai gratuit
              </Link>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <Hero />

      {/* Features Section */}
      <Features />

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Fonctionnement en 3 étapes
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Une plateforme intuitive conçue pour simplifier la gestion de vos établissements
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                number: "01",
                title: "Inscription du superviseur",
                description: "Créez votre compte et configurez votre profil en quelques minutes",
                bgColor: "bg-blue-100",
                textColor: "text-blue-600"
              },
              {
                number: "02",
                title: "Ajout des établissements",
                description: "Créez vos établissements et invitez vos secrétaires",
                bgColor: "bg-green-100",
                textColor: "text-green-600"
              },
              {
                number: "03",
                title: "Gestion financière",
                description: "Suivez vos transactions et générez des rapports automatiques",
                bgColor: "bg-purple-100",
                textColor: "text-purple-600"
              }
            ].map((step, index) => (
              <div 
                key={index} 
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 ${step.bgColor} ${step.textColor} rounded-full text-2xl font-bold mb-6`}>
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-600">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <Pricing />

      {/* Testimonials Section */}
      <Testimonials />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="mb-8">
              {/* Logo dans la section CTA */}
              <div className="flex justify-center mb-6">
                <Logo size="xl" showText={false} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Prêt à révolutionner votre gestion d'établissements ?
              </h2>
              <p className="text-xl text-blue-100">
                Rejoignez des centaines de superviseurs qui font confiance à EXELIUS
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link
                to="/register"
                className="px-8 py-4 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 inline-flex items-center justify-center text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <Building2 className="mr-2 h-5 w-5" />
                Essai gratuit 14 jours
              </Link>
              <Link
                to="/contact"
                className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white/10 transition-all duration-300 text-lg"
              >
                Demander une démo
              </Link>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 max-w-2xl mx-auto">
              <p className="text-blue-200 font-medium mb-3">
                🎁 Inclus dans votre essai gratuit :
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm text-blue-100">
                <div className="flex items-center justify-center">
                  <span className="inline-block w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                  Accès à toutes les fonctionnalités
                </div>
                <div className="flex items-center justify-center">
                  <span className="inline-block w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                  Support prioritaire
                </div>
                <div className="flex items-center justify-center">
                  <span className="inline-block w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                  Aucune carte requise
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-5 gap-8">
            {/* Brand column */}
            <div className="md:col-span-2">
              {/* Logo dans le footer */}
              <Logo size="xl" showText={false} className="mb-6" />
              
              <p className="text-gray-400 mb-6">
                Plateforme de gestion financière pour établissements professionnels. 
                Simplifiez la gestion de vos établissements et de vos équipes.
              </p>
              <div className="flex space-x-4">
                {['Twitter', 'LinkedIn', 'Facebook'].map((social) => (
                  <a
                    key={social}
                    href="#"
                    className="text-gray-400 hover:text-white transition-colors"
                    aria-label={social}
                  >
                    {social}
                  </a>
                ))}
              </div>
            </div>

            {/* Links columns */}
            {[
              {
                title: "Produit",
                links: [
                  { name: "Fonctionnalités", href: "#features" },
                  { name: "Tarifs", href: "#pricing" },
                  { name: "API", href: "/api" },
                  { name: "Documentation", href: "/docs" }
                ]
              },
              {
                title: "Entreprise",
                links: [
                  { name: "À propos", href: "/about" },
                  { name: "Blog", href: "/blog" },
                  { name: "Carrières", href: "/careers" },
                  { name: "Presse", href: "/press" }
                ]
              },
              {
                title: "Support",
                links: [
                  { name: "Centre d'aide", href: "/help" },
                  { name: "Contact", href: "/contact" },
                  { name: "RGPD", href: "/gdpr" },
                  { name: "CGU", href: "/terms" }
                ]
              }
            ].map((column, index) => (
              <div key={index}>
                <h4 className="font-semibold text-lg mb-4">
                  {column.title}
                </h4>
                <ul className="space-y-3">
                  {column.links.map((link) => (
                    <li key={link.name}>
                      {link.href.startsWith('#') ? (
                        <a
                          href={link.href}
                          className="text-gray-400 hover:text-white transition-colors"
                        >
                          {link.name}
                        </a>
                      ) : (
                        <Link
                          to={link.href}
                          className="text-gray-400 hover:text-white transition-colors"
                        >
                          {link.name}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-800 my-8"></div>

          {/* Bottom section */}
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0 text-center md:text-left">
              <p className="text-gray-400">
                © {currentYear} MindSet International, LLC. Tous droits réservés.
              </p>
              <p className="text-gray-500 text-sm mt-1">
                Delaware, USA • contact@mindset-international.com • +1 217 393 3393
              </p>
            </div>

            <div className="flex items-center space-x-6 text-sm text-gray-400">
              <Link to="/privacy" className="hover:text-white">
                Confidentialité
              </Link>
              <Link to="/cookies" className="hover:text-white">
                Cookies
              </Link>
              <Link to="/accessibility" className="hover:text-white">
                Accessibilité
              </Link>
              <Link to="/sitemap" className="hover:text-white">
                Plan du site
              </Link>
            </div>
          </div>

          {/* Badges */}
          <div className="mt-8 pt-8 border-t border-gray-800 flex flex-wrap justify-center gap-6 text-gray-500 text-sm">
            <div className="flex items-center">
              <div className="h-8 w-8 rounded-full bg-green-900/20 flex items-center justify-center mr-2">
                <span className="text-green-400 font-bold">RGPD</span>
              </div>
              <span>Conforme RGPD</span>
            </div>
            <div className="flex items-center">
              <div className="h-8 w-8 rounded-full bg-blue-900/20 flex items-center justify-center mr-2">
                <span className="text-blue-400 font-bold">SSL</span>
              </div>
              <span>Chiffré SSL</span>
            </div>
            <div className="flex items-center">
              <div className="h-8 w-8 rounded-full bg-purple-900/20 flex items-center justify-center mr-2">
                <span className="text-purple-400 font-bold">ISO</span>
              </div>
              <span>ISO 27001</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;